import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, HelpCircle, Loader2, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  checkUrlEmbeddable,
  clearUrlEmbedCache,
  type UrlEmbedResult,
} from "@/lib/scenarios/urlEmbedCheck";

/**
 * Probe a URL's iframe-embeddability. Shared by the display picker and the
 * learner-display preview so both show the same verdict (results are cached
 * per URL in `urlEmbedCheck`).
 */
export function useUrlEmbedCheck(url?: string) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<UrlEmbedResult | null>(null);

  const run = useCallback(
    (target: string) => {
      let cancelled = false;
      setLoading(true);
      setResult(null);
      checkUrlEmbeddable(target)
        .then((r) => {
          if (!cancelled) setResult(r);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    },
    [],
  );

  useEffect(() => {
    if (!url) {
      setResult(null);
      setLoading(false);
      return;
    }
    return run(url);
  }, [url, run]);

  const recheck = useCallback(() => {
    if (!url) return;
    clearUrlEmbedCache(url);
    run(url);
  }, [url, run]);

  return { loading, result, recheck };
}

export const EMBED_COPY: Record<
  UrlEmbedResult["verdict"],
  { text: string; tone: "ok" | "warn" | "error" | "muted" }
> = {
  ok: {
    text: "This page can be shown inside the session.",
    tone: "ok",
  },
  blocked: {
    text:
      "This site blocks embedding. Learners will see an “Open in a new tab” card instead of the page — use a PDF, image or a different URL to show it inline.",
    tone: "warn",
  },
  unreachable: {
    text: "We couldn't reach this URL, so learners won't see anything.",
    tone: "error",
  },
  unknown: {
    text: "We couldn't verify this page — check the preview before publishing.",
    tone: "muted",
  },
};

const TONE_CLASS = {
  ok: "text-emerald-600 dark:text-emerald-400",
  warn: "text-amber-600 dark:text-amber-400",
  error: "text-destructive",
  muted: "text-muted-foreground",
} as const;

function VerdictIcon({ tone }: { tone: "ok" | "warn" | "error" | "muted" }) {
  const cls = "h-3.5 w-3.5 shrink-0 mt-0.5";
  if (tone === "ok") return <CheckCircle2 className={cls} />;
  if (tone === "warn") return <AlertTriangle className={cls} />;
  if (tone === "error") return <XCircle className={cls} />;
  return <HelpCircle className={cls} />;
}

export function UrlEmbedStatus({
  loading,
  result,
  onRecheck,
  className,
}: {
  loading: boolean;
  result: UrlEmbedResult | null;
  onRecheck?: () => void;
  className?: string;
}) {
  if (loading) {
    return (
      <p className={cn("flex items-center gap-1.5 text-xs text-muted-foreground", className)}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Checking if this page can be embedded…
      </p>
    );
  }
  if (!result) return null;
  // A working URL needs no confirmation — only surface problems.
  if (result.verdict === "ok") return null;
  const copy = EMBED_COPY[result.verdict];
  return (
    <p className={cn("flex items-start gap-1.5 text-xs", TONE_CLASS[copy.tone], className)}>
      <VerdictIcon tone={copy.tone} />
      <span>
        {copy.text}
        {onRecheck && (
          <button
            type="button"
            onClick={onRecheck}
            className="ml-1.5 underline hover:no-underline"
          >
            Re-check
          </button>
        )}
      </span>
    </p>
  );
}